import React from "react";
import "../styles/HubShell.css";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

// White Resolute logo
const RESOLUTE_LOGO =
  "https://static.wixstatic.com/media/27ada9_707c1d039bba497caf03ec89983c9f70~mv2.png";

const WIDGET_TYPE = "HUB_WIDGET";

const DraggableWidgetCard = ({ id, index, moveWidget, children }) => {
  const ref = React.useRef(null);

  const [, drop] = useDrop({
    accept: WIDGET_TYPE,
    hover: (item) => {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      moveWidget(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: WIDGET_TYPE,
    item: () => ({ id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <article
      ref={ref}
      className="hub-widget-card"
      style={{ opacity: isDragging ? 0.6 : 1 }}
    >
      {children}
    </article>
  );
};

const HubShell = ({ user, onSignOut }) => {
  const initials = user.initials || "--";
  const displayName = user.name || user.email;
  const email = user.email;

  // Order of widgets on the hub
  const [widgets, setWidgets] = React.useState(["weather", "news", "store"]);

  const moveWidget = (from, to) => {
    setWidgets((prev) => {
      const next = [...prev];
      const [removed] = next.splice(from, 1);
      next.splice(to, 0, removed);
      return next;
    });
  };

  const renderWidget = (key, index) => {
    if (key === "weather") {
      return (
        <DraggableWidgetCard
          key={key}
          id={key}
          index={index}
          moveWidget={moveWidget}
        >
          <div className="hub-widget-header">
            <span className="hub-widget-title">
              <i className="bi bi-cloud-sun" /> Weather
            </span>
            <span className="hub-widget-pill">Clay Cross</span>
          </div>
          <div className="hub-widget-body">
            <p className="hub-widget-text">
              Configure this to use a live weather API (Open‑Meteo or similar)
              for Clay Cross.
            </p>
          </div>
        </DraggableWidgetCard>
      );
    }

    if (key === "news") {
      return (
        <DraggableWidgetCard
          key={key}
          id={key}
          index={index}
          moveWidget={moveWidget}
        >
          <div className="hub-widget-header">
            <span className="hub-widget-title">
              <i className="bi bi-megaphone" /> Latest news
            </span>
            <span className="hub-widget-pill">Internal</span>
          </div>
          <div className="hub-widget-body">
            <p className="hub-widget-text">
              Internal updates and social posts will appear here. Later you can
              connect this to your CMS or a simple JSON feed.
            </p>
          </div>
        </DraggableWidgetCard>
      );
    }

    if (key === "store") {
      return (
        <DraggableWidgetCard
          key={key}
          id={key}
          index={index}
          moveWidget={moveWidget}
        >
          <div className="hub-widget-header">
            <span className="hub-widget-title">
              <i className="bi bi-layout-text-window-reverse" /> Widget store
            </span>
            <span className="hub-widget-pill">Coming soon</span>
          </div>
          <div className="hub-widget-body hub-widget-store">
            <p className="hub-widget-text">
              Choose which widgets appear on your hub. Drag to reorder, and soon
              you’ll be able to turn widgets on or off directly from here.
            </p>
            <div className="hub-widget-tags">
              <span className="hub-tag hub-tag-active">
                <i className="bi bi-check2-square" /> Approvals
              </span>
              <span className="hub-tag">
                <i className="bi bi-cloud-sun" /> Weather
              </span>
              <span className="hub-tag">
                <i className="bi bi-megaphone" /> News
              </span>
              <span className="hub-tag">
                <i className="bi bi-briefcase" /> Tools
              </span>
            </div>
          </div>
        </DraggableWidgetCard>
      );
    }

    return null;
  };

  return (
    <div className="hub-root">
      <header className="hub-header">
        <div className="hub-header-left">
          <img src={RESOLUTE_LOGO} alt="Resolute" className="hub-logo-img" />
          <div>
            <div className="hub-title">Resolute Hub</div>
            <div className="hub-subtitle">
              Signed in as <span>{email}</span>
            </div>
          </div>
        </div>
        <div className="hub-header-right">
          <div
            className="hub-avatar"
            style={
              user.avatar
                ? {
                    backgroundImage: `url(${user.avatar})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    color: "transparent",
                  }
                : {}
            }
          >
            {!user.avatar && initials}
          </div>
          <button className="hub-signout" onClick={onSignOut}>
            <i className="bi bi-box-arrow-right" /> <span>Sign out</span>
          </button>
        </div>
      </header>

      <main className="hub-main">
        <DndProvider backend={HTML5Backend}>
          <section className="hub-gradient-shell">
            {/* Profile box + KPIs */}
            <div className="hub-welcome-top">
              <div className="hub-profile">
                <div
                  className="hub-profile-avatar"
                  style={
                    user.avatar
                      ? {
                          backgroundImage: `url(${user.avatar})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          color: "transparent",
                        }
                      : {}
                  }
                >
                  {!user.avatar && initials}
                </div>
                <div className="hub-profile-info">
                  <div>{displayName}</div>
                  <div>{email}</div>
                  <div>Staff hub · Approvals, tools and updates</div>
                </div>
              </div>
              <div className="hub-kpi-row">
                <div className="hub-kpi-card">
                  <div className="hub-kpi-label">Files in review</div>
                  <div className="hub-kpi-value">0</div>
                  <div className="hub-kpi-sub">
                    Hook this to the approval system soon
                  </div>
                </div>
                <div className="hub-kpi-card">
                  <div className="hub-kpi-label">Needs attention</div>
                  <div className="hub-kpi-value">0</div>
                  <div className="hub-kpi-sub">
                    Pending or changes requested files
                  </div>
                </div>
                <div className="hub-kpi-card">
                  <div className="hub-kpi-label">Approved this week</div>
                  <div className="hub-kpi-value">0</div>
                  <div className="hub-kpi-sub">Signed off in last 7 days</div>
                </div>
              </div>
            </div>

            {/* Draggable widgets row */}
            <div className="hub-widgets-row">
              {widgets.map((w, index) => renderWidget(w, index))}
            </div>

            {/* Apps "carousel" row – approval system and other tools */}
            <div className="hub-apps-row">
              <div className="hub-apps-header">
                <h2>Apps</h2>
                <p>Quick shortcuts to the tools you use most.</p>
              </div>
              <div className="hub-apps-grid">
                <button
                  className="hub-app-tile"
                  // onClick={...} hook this to your approval shell route
                >
                  <i className="bi bi-check2-square" />
                  <span>Approval Management</span>
                </button>
                <button className="hub-app-tile">
                  <i className="bi bi-envelope" />
                  <span>Email</span>
                </button>
                <button className="hub-app-tile">
                  <i className="bi bi-clipboard-check" />
                  <span>ServiceM8</span>
                </button>
                <button className="hub-app-tile hub-app-disabled">
                  <i className="bi bi-people" />
                  <span>Team updates</span>
                </button>
                <button className="hub-app-tile hub-app-disabled">
                  <i className="bi bi-journal-text" />
                  <span>Guides & docs</span>
                </button>
              </div>
            </div>
          </section>
        </DndProvider>
      </main>
    </div>
  );
};

export default HubShell;
